import {uniqBy} from 'lodash';
import {formatUrl} from '@teemill/common/helpers';
import {BaseClass} from '@teemill/common/classes';
import ChatMessage from './chatMessage';

export default class Commentable extends BaseClass {
  comments = [];

  constructor(comments = []) {
    super();

    this.comments = ChatMessage.map(comments) || [];
  }

  /**
   * @return {Number}
   */
  get commentCount() {
    return this.commentsWithReplies.length;
  }

  /**
   * @return {Boolean}
   */
  get hasUnreadComments() {
    return this.unreadComments.length > 0;
  }

  /**
   * @return {ChatMessage[]}
   */
  get unreadComments() {
    return this.comments.filter(comment => comment.unread);
  }

  /**
   * @return {ChatMessage[]}
   */
  get readComments() {
    return this.comments.filter(comment => !comment.unread);
  }

  /**
   * @return {Number}
   */
  get uniqueOwnersCount() {
    return uniqBy(this.commentsWithReplies, 'owner.username').length;
  }

  /**
   * @return {ChatMessage[]}
   */
  get commentsWithReplies() {
    const replies = this.comments
      .map(comment => comment.replies)
      .reduce(
        (accumulator, currentValue) => accumulator.concat(currentValue),
        []
      );

    return this._sortComments(this.comments.concat(replies));
  }

  /**
   * @param {ChatMessage}
   *
   * @return {ChatChannel}
   */
  addComment(comment) {
    comment = ChatMessage.map(comment);

    if (comment.isReply) {
      const parent = this.comments.find(c => c.id === comment.parent);

      // If we can't find the parent in the chat comment list
      // we can assume that its simply not on the page (earlier in history)
      // and therefore, we don't need to update or add the comment
      if (!parent) {
        return this;
      }

      // Add the parent model to the reply - the resource will be returned
      // with only the ID for efficiencies' sakes
      comment.parent = parent;

      parent.replies.push(comment);

      parent.replies = this._sortComments(parent.replies);
    } else {
      this.comments.push(comment);

      this.comments = this._sortComments(this.comments);
    }

    return this;
  }

  /**
   * @param {ChatMessage}
   *
   * @return {ChatChannel}
   */
  removeComment(comment) {
    comment = ChatMessage.map(comment);

    if (comment.isReply) {
      const parent = this.comments.find(c => c.id === comment.parent);

      // If we can't find the parent in the chat comment list
      // we can assume that its simply not on the page (earlier in history)
      // and therefore, we don't need to update the comment
      if (!parent) {
        return this;
      }

      parent.replies = parent.replies.filter(r => r.id !== comment.id);
    } else {
      this.comments = this.comments.filter(r => r.id !== comment.id);
    }

    return this;
  }

  /**
   * @param {ChatMessage}
   *
   * @return {void}
   */
  updateComment(comment) {
    comment = ChatMessage.map(comment);

    if (comment.isReply) {
      const parent = this.comments.find(m => m.id === comment.parent);

      // If we can't find the parent in the chat comment list
      // we can assume that its simply not on the page (earlier in history)
      // and therefore, we don't need to update the comment
      if (!parent) {
        return;
      }

      // Add the parent model to the reply - the resource will be returned
      // with only the ID for efficiencies' sakes
      comment.parent = parent;

      const replyIndex = parent.replies.findIndex(m => m.id === comment.id);
      const reply = parent.replies[replyIndex];

      // There is a small chance that a race condition can occur, causing
      // the child index to disappear before its updated. eg. one person deletes
      // a comment at the same time as another reacts to it. If the former
      // resolves before the latter - this index will not exist.
      if (!reply) {
        return;
      }

      Object.assign(reply, {...comment, unread: reply.unread});
    } else {
      const commentIndex = this.comments.findIndex(m => m.id === comment.id);

      // If we can't find the comment in the chat comment list
      // we can assume that its simply not on the page (earlier in history)
      // and therefore, we don't need to update the comment
      if (commentIndex < 0) {
        return;
      }

      const commentObject = this.comments[commentIndex];

      Object.assign(commentObject, {...comment, unread: commentObject.unread});
    }
  }

  /**
   * @return {ChatChannel}
   */
  shift() {
    this.comments.shift();

    return this;
  }

  uploadTemporaryFile(file) {
    const form = new FormData();

    form.append(typeof file === 'string' ? 'image' : 'file', file);
    form.append('commentable', this.code || this.id);
    form.append('commentableType', this.commentableType);

    return $axios
      .post(formatUrl('/omnis/v3/comment/temporary-file/create/'), form)
      .success(pendingImage => pendingImage)
      .validation()
      .handle(413, () => {
        snackbar.error(
          'File size was too large to upload, files uploaded should be less than 20MB'
        );
      })
      .oops(
        'Sorry, something went wrong uploading that image, try re-uploading'
      )
      .output();
  }

  /**
   * @param {Person} unfilteredPerson
   *
   * @return {Number}
   */
  getVisibleCommentCount(unfilteredPerson = null) {
    return this.getVisibleCommentsWithReplies(unfilteredPerson).length;
  }

  /**
   * @param {Person} unfilteredPerson
   *
   * @return {Number}
   */
  getVisibleUniqueOwnersCount(unfilteredPerson = null) {
    return uniqBy(
      this.getVisibleCommentsWithReplies(unfilteredPerson),
      'owner.username'
    ).length;
  }

  /**
   * @param {Person} unfilteredPerson
   *
   * @return {ChatMessage[]}
   */
  getVisibleCommentsWithReplies(unfilteredPerson = null) {
    return this.commentsWithReplies.filter(
      comment => !comment.shouldHide || comment.ownedBy(unfilteredPerson)
    );
  }

  /**
   * @return {ChatMessage[]}
   */
  _sortComments(comments) {
    return comments.sort(
      (a, b) => (a.refocusedAt || a.timestamp) - (b.refocusedAt || b.timestamp)
    );
  }
}
